import * as React from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";

// <=== 💸  MUI Stuff  💸 ===>
import ListItem from "@mui/material/ListItem";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Switch from '@mui/material/Switch';
import Drawer from '@mui/material/Drawer';
import Slide from '@mui/material/Slide';
import { Container, Typography, FormControlLabel } from "@mui/material";

import { GoldButton, HeroVideoBackground, dotaSpottoTheme } from "@ds-components/General/dotaspottotheme";
import { RequestForm } from "@ds-components/General/Forms";
import { HeroLookup } from "@ds-types/dotes";
import { HeroDataService } from "@ds-types/responsestate";
import useDotaData from "@ds-services/dota/mergedotadata";
import { setLocalStorage, getLocalStorage } from "@ds-services/helpers";
import * as Analytics from "@ds-services/analytics/analytics";
import { useAuthStore } from "@/store/authStore";

const spotifyLogoBlack = "https://475xwd0koj.ucarecd.net/2c077297-59a8-422d-98c8-2007310119b5/Primary_Logo_Black_RGB.svg";
const spotifyLogoGreen = "https://475xwd0koj.ucarecd.net/6649b0d2-df18-4abe-b883-a8e8534b2471/Primary_Logo_Green_RGB.svg";
const defaultVideo = "https://475xwd0koj.ucarecd.net/b2c62955-0b45-41df-8adc-800ebd658468/sparky.mp4";

const getVideoType = (videoUrl: string) => {
	return videoUrl.includes(".mp4") ? "video/mp4" : "video/webm";
};

const getHeroVideoUrl = (hero: HeroLookup) => {
	if (hero.video) return hero.video;
	if (!hero.img) return "";
	return hero.img
		.replace("png", "webm")
		.replace("images", "videos")
		.replace("heroes/", "heroes/renders/");
};



interface HeroSelectorProps {
	herolist: HeroDataService<HeroLookup[]>;
	sendDataToParent: (data: any) => void;
	playlistloading: boolean;
}

const HeroSelector: React.FC<HeroSelectorProps> = ({ herolist, sendDataToParent, playlistloading }) => {
	const { status, login, showToast } = useAuthStore();
	// Compute and sort hero options
	const options = useMemo(() => {
		if (herolist.status !== "loaded") return [];
		if (herolist.status === "loaded" && herolist.payload) {

			return herolist.payload.map((hero: HeroLookup) => {
				const firstLetter = hero.localized_name[0].toUpperCase();
				return {
					firstLetter: /[a-zA-Z]/.test(firstLetter) ? firstLetter : "0-9",
					...hero,
				};
			})
				.sort((a, b) => a.firstLetter.localeCompare(b.firstLetter));
		}
	}, [herolist]);
	// Set default hero from local storage or fallback to the first hero

	const defaultHero = useMemo(() => {
		const storedHeroData: HeroLookup | null = getLocalStorage("the_hero") ? JSON.parse(getLocalStorage("the_hero") as string) as HeroLookup : null;
		if (storedHeroData && options?.length) {
			const matchingHero = options.find((option) =>
				option.name === storedHeroData.name ||
				option.localized_name === storedHeroData.localized_name ||
				option.displayName === storedHeroData.displayName
			);
			if (matchingHero) {
				return {
					...matchingHero,
					action: storedHeroData.action,
				};
			}
		}
		return storedHeroData ? {
			...storedHeroData,
			video: storedHeroData.video || getHeroVideoUrl(storedHeroData),
		} : options?.[0];
	}, [options]);

	const [value, setValue] = React.useState<HeroLookup | null | undefined>(defaultHero);
	const [tempVal, setTempVal] = React.useState<HeroLookup | null | undefined>(value);
	const [inputValue, setInputValue] = React.useState("");
	const [showBio, setShowBio] = React.useState(false);
	const [isPlaylistLoading, setIsPlaylistLoading] = React.useState(playlistloading);
	const [isSpotifyButtonHovered, setIsSpotifyButtonHovered] = React.useState(false);
	const videoRef = useRef<HTMLVideoElement>(null);
	const displayedHero = tempVal ?? value;

	useEffect(() => {
		const valueIsDefaultHero = value && (
			value.name === defaultHero?.name ||
			value.localized_name === defaultHero?.localized_name ||
			value.displayName === defaultHero?.displayName
		);
		if (defaultHero && (!value || (valueIsDefaultHero && value.video !== defaultHero.video))) {
			setValue(defaultHero);
			setTempVal(defaultHero);
		}
	}, [defaultHero, value]);

	useEffect(() => {
		videoRef.current?.load();
	}, [displayedHero]);

	useEffect(() => {
		setIsPlaylistLoading(playlistloading)
	}, [playlistloading])

	// useEffect(() => {
	// 	if (options && options.length > 0) {
	// 		console.log(`🥮grim__options==> ==> ==>options`);
	// 		console.log({ grim__options: options });
	// 		// let index = 0;
	// 		// const intervalId = setInterval(() => {
	// 		// 	if (index >= options.length) {
	// 		// 		clearInterval(intervalId);
	// 		// 		return;
	// 		// 	}
	// 		// 	const option = options[index];
	// 		// 	const heroName = option.localized_name;
	// 		// 	const prefetchLinkId = `${heroName}hero-video-prefetch`;
	// 		// 	if (document.getElementById(prefetchLinkId)) return;
	// 		// 	const videoPrefetch = document.createElement('link');
	// 		// 	videoPrefetch.id = `${heroName}hero-video-prefetch`;
	// 		// 	videoPrefetch.rel = 'prefetch';
	// 		// 	videoPrefetch.href = option.video;
	// 		// 	document.head.appendChild(videoPrefetch);
	// 		// 	index++;
	// 		// }, 250);
	// 	}
	// }, [options]);

	/**
	 * Other Funks
	 */
	// Handle button click for hero selection
	const handleHeroUpdate = useCallback(
		async (e: React.MouseEvent<HTMLButtonElement>) => {
			try {
				const currentTargetId = e.currentTarget.id;
				if (status !== "authenticated") {
					showToast("Please log in to Spotify first.", "info");
				}
				else if (currentTargetId && currentTargetId === "hero-select-button") {
					if (value && !value?.action) {
						value.action = "search";
					}
					document.title = `${value?.displayName} - Dota Tunes`;
					sendDataToParent(value);
					if (value?.displayName) {
						Analytics.trackHeroSelected(value?.displayName)
					}
				}
			} catch (error) {
				console.error("Error during hero selection update:", error);
			}
		},
		[value, sendDataToParent, showToast, status]
	);
	//	Handle Show Biography
	const handleShowBio = (event: React.ChangeEvent<HTMLInputElement>) => {
		setShowBio(event.target.checked);
	};
	const backgroundVideo = displayedHero?.video || defaultVideo;
	return (

		<Box>
			{
				(status === "checking" || status === "loggedout" || !status) && (
					<Drawer
						sx={{
							width: "100%",
							flexShrink: 0,
							overflow: "hidden",
							'& .MuiDrawer-paper': {
								boxSizing: 'border-box',
								backgroundImage: `linear-gradient(to bottom, transparent, ${dotaSpottoTheme.primary})`,
								backgroundColor: "rgba(0,0,0,0.69)",
								border: "none",
								backdropFilter: 'blur(5px)'
							},
						}}
						variant="permanent"
						anchor="bottom"
					>
						<Container sx={{
							mt: 2,
							height: "100vh",
							display: "flex",
							flexFlow: "column nowrap",
							justifyContent: "space-around",
							alignItems: "center",
							gap: 2,
						}}>
							<Typography sx={{
								bgcolor: "transparent",
								fontSize: "1.25rem",
								color: '#fff',

							}}
								component="div" className="static-text">
								<Box sx={{
									maxHeight: "250px",
									position: "relative",
									display: "flex",
									justifyContent: "center",
									alignItems: "center"
								}}>
									<img
										style={{
											objectFit: "contain",
											maxWidth: "125px",
											filter: "blur(1px) drop-shadow(0 0 2px black)",
											boxShadow: "0 0 15px 3px rgba(0, 0, 0, 0.45)",
											borderRadius: "50%",
											// backgroundImage: "radial-gradient(circle, transparent, rgba(0,0,0,0.25))"
											backgroundImage: `radial-gradient(circle, transparent, rgba(0,0,0,0.48))`
										}}
										src="https://475xwd0koj.ucarecd.net/5584f826-3a46-4fbb-a383-331eeb692fd3/helmuttreduxtransparent.webp" alt="Dota Tunes Logo" />
								</Box>
								<h1>Welcome to Dota Tunes</h1>
								<Box sx={{
									fontSize: '1.1rem',
									fontWeight: 600,
									borderRadius: '8px',
									color: 'white',
									border: '1px solid rgba(167, 39, 20, 0.6)',
									backgroundColor: 'rgba(2, 2, 2, 0.6)',
									backdropFilter: 'blur(27px)',
									padding: '16px 20px',
								}}>
									Spotify limited its API availability to just <a href="https://developer.spotify.com/documentation/web-api/concepts/quota-modes">5 seats</a>. Because of this, my hopes for this project have been ulted. If you are still interested in taking it for a spin, please share your Spotify account email below and I'll add you as a user:
									<RequestForm />
								</Box>

								<p>Create a playlist based on a Dota Hero with just a few steps:</p>
								<ol style={{ listStyleType: "upper-roman" }}>
									<li>Log into Spotify</li>
									<li>Agree to allow Dota Tunes to create Playlists</li>
									<li>Select your Hero</li>
									<li>Click "Generate Playlist"</li>
								</ol>
							</Typography>
							<Box sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 1,
								position: "relative",
								zIndex: 10,
							}}>
								<GoldButton
									id="spotify-login-link"
									variant="contained"
									onClick={() => void login()}
									onMouseEnter={() => setIsSpotifyButtonHovered(true)}
									onMouseLeave={() => setIsSpotifyButtonHovered(false)}
									onFocus={() => setIsSpotifyButtonHovered(true)}
									onBlur={() => setIsSpotifyButtonHovered(false)}
								>
									<span style={{
										paddingRight: "10px",
										maxHeight: "24px",
										aspectRatio: 1
									}}>
										<img
											src={isSpotifyButtonHovered ? spotifyLogoGreen : spotifyLogoBlack}
											alt="Spotify Logo"
											className="spotify-btn-icon"
										/>
									</span> Log into Spotify
								</GoldButton>

							</Box>
							<HeroVideoBackground data-active={isPlaylistLoading ? '' : undefined}
								sx={{
									width: "100%",
									maxHeight: "100vh",
									position: "absolute",
									top: "0 !important",
									opacity: 0.25,
									filter: "contrast(1) hue-rotate(100deg) saturate(15)",
								}}>
								<video
									autoPlay
									loop
									muted
									style={{
										objectFit: "cover",
										flex: "1",
										height: "auto",
									}}
								>
									<source
										src={defaultVideo}
										type={getVideoType(defaultVideo)}
									/>
									Your browser does not support the video tag.
								</video>
							</HeroVideoBackground>
						</Container>
					</Drawer>
				)
			}
			{
				!options || options && options.length === 0 && (
					<Container>
						Where have all the heroes gone?
					</Container>

				)
			}
			{
				options && options.length > 0 && (
					<>
						<Container sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							flexFlow: {
								xs: "column nowrap",
								md: "row nowrap"
							},
							opacity: isPlaylistLoading ? 0 : 1,
							gap: 3,
							maxWidth: 960,
							margin: "auto",
						}}>
							<Box component="form" sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								flexFlow: {
									xs: "column nowrap",
									md: "row nowrap"
								},
								gap: 3,
								margin: "auto",
								width: '100%',
								order: {
									xs: 0,
									md: 1
								},
							}}>
								<Autocomplete
									slotProps={{
										paper: {
											sx: {
												'& .MuiAutocomplete-listbox': {
													'& .MuiAutocomplete-option:hover': {
														backgroundColor: "gold",
														color: dotaSpottoTheme.primary,
													},
												},
											},
										},
									}}
									sx={
										(theme) => ({
											width: '100%',
											flex: 1,
											"& input": {
												fontSize: "1.5rem",
											},
											"& label": {
												bgcolor: "transparent",
												"&.Mui-disabled": {
													display: "none",
												}
											}
										})}
									disabled={status === "checking"}
									value={value ?? ""}
									disableClearable
									autoComplete={true}
									isOptionEqualToValue={(option: any, value: any) =>
										option.id === value.id
									}
									onChange={(event: any, newValue: HeroLookup | null) => {
										if (newValue) {
											Analytics.trackHeroToggled(newValue.displayName ?? "")
											setValue(newValue);
											setLocalStorage("the_hero", JSON.stringify(newValue));
										}
									}}
									inputValue={inputValue}
									onInputChange={(event, newInputValue) => {
										setInputValue(newInputValue);
										if (newInputValue) {
											const formattedInput = newInputValue.toLowerCase().replace(/ /g, "_");
											sendDataToParent(formattedInput);
										}
									}}
									// className={value}
									options={options ?? []}
									groupBy={(option: any) => option.firstLetter}
									getOptionLabel={(option: any) => option.displayName}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Select Your Hero"
											className="dota-gas"
											variant="outlined"
											fullWidth
											sx={{
												borderRadius: `6px`,
												backgroundColor: "rgba(69,69,69,0.69)",
												backdropFilter: 'blur(5px)'
											}}
										/>
									)}

									renderOption={(props, option) => {
										return (
											<ListItem
												slotProps={{}}
												{...props}
												key={option.localized_name}
												disablePadding
												onMouseEnter={() => {
													if (option.video) {
														setTempVal(option);
													}
												}}
												onMouseLeave={() => {
													setTempVal(null);
												}}
											>
												<Avatar
													src={option.icon}
													className={`heroface`}
													sx={{ mr: 2 }}
												/>
												<React.Fragment>
													<Box>
														<span>{option.displayName}</span>
													</Box>
												</React.Fragment>
											</ListItem>
										)
									}}
								// render
								/>

								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										justifyContent: "center",
										alignItems: "flex-end",
									}}
								>
									<Button
										sx={{
											"&.Mui-disabled": {
												backgroundColor: "rgba(69,69,69,0.69)",
												color: "rgba(255,255,255,0.23)",
												borderColor: "rgba(169,169,169,0.69)",
												opacity: 0.45
											}
										}}
										variant="contained"
										size="large"
										id="hero-select-button"
										onClick={handleHeroUpdate}
										disabled={status !== "authenticated"}
									>
										⚡ Generate Playlist
									</Button>
								</Box>
							</Box>
							{displayedHero && displayedHero.icon && (
								<Avatar
									sx={{
										order: 0,
										display: {
											md: status === "authenticated" ? "block" : "none",
											xs: "none",
										}
									}}
									src={displayedHero.icon}
									className={`heroface`}
								/>
							)}
						</Container>
						<Container sx={{
							textAlign: "center"
						}}>
							<HeroVideoBackground
								id="hero-video-bg-container"
								data-active={isPlaylistLoading ? '' : undefined}
							>
								<video
									key={backgroundVideo}
									autoPlay
									loop
									muted
									style={{
										objectFit: "contain",
										flex: "1",
										height: "auto"
									}}
									ref={videoRef}
									id="hero-video-bg"
									poster={displayedHero?.video ? displayedHero.video.replace("webm", "png") : undefined}
								>
									<source
										src={backgroundVideo}
										type={getVideoType(backgroundVideo)}
									/>
									Your browser does not support the video tag.
								</video>
							</HeroVideoBackground>
							{displayedHero && displayedHero.bio ? (
								<>
									<Box sx={{
										fontSize: "3rem",
										// alignItems: "center",
										// justifyContent: "center",
										// display: "flex",
										height: "100%",
										mb: 2,
										opacity: isPlaylistLoading ? 0 : 1,
										// md: {

										// },
										// backgroundColor: "rgba(0, 0, 0, 0.36)",
										// background: "none",
										"& h2": {
											// md: {

											// }
											textAlign: "center",
											textShadow: "0 3px 6px black",
											display: "flex",
											flexFlow: "column nowrap",
											lineHeight: "1",
											zIndex: 1,
										},
										// "& h2:after": {
										// 	content: "''",
										// 	// position: "absolute",
										// 	width: "100%",
										// 	height: "8px",
										// 	marginTop: "-11px",
										// 	background: dotaSpottoTheme.primary,
										// 	zIndex: -1,
										// }
									}}
									>
										<Typography variant="h2" sx={{
											textAlign: "center",
											my: {
												md: 8,
												xs: 4.5
											},
											fontSize: {
												sm: "5rem",
												xs: "3rem"
											}
										}}>
											{displayedHero.displayName}
										</Typography>
										{
											displayedHero.hype && (
												<Box
													sx={{
														textAlign: "left",
														fontSize: {
															sm: "2rem",
															xs: "1rem"
														},
													}}
													// style={{ fontWeight: "bold" }}
													dangerouslySetInnerHTML={{
														__html: displayedHero.hype
													}}
												/>
											)
										}
									</Box>
									<FormControlLabel
										sx={{
											opacity: isPlaylistLoading ? 0 : 1,
											'& .MuiFormControlLabel-label': {
												backgroundColor: 'transparent',
											}
										}}
										control={
											<Switch
												sx={{
													width: "88px",
													overflow: "visible",
													'& .MuiSwitch-switchBase': {
														padding: 0,
														transform: 'translateX(0)',
														height: '100%',
														aspectRatio: 1,
														'&:hover': {
															backgroundColor: 'transparent',
															'& .MuiSwitch-thumb': {
																boxShadow: '0 0 5px 2px gold'
															}
														},
														'& .MuiSwitch-thumb': {
															backgroundColor: '#001e3c',
															width: 25,
															height: 25,
															aspectRatio: 1 / 1,
															borderRadius: '100%',
															backgroundRepeat: 'no-repeat',
															backgroundPosition: 'center',
															backgroundSize: 'contain',
															backgroundImage: `url('${displayedHero.icon}')`,
															border: '1px solid rgba(167, 39, 20, 1)',
															boxShadow: '0 0 12px 4px rgba(167, 39, 20, 0)',
															transition: 'box-shadow 0.5s ease, border 0.35s ease',
														},

														// Checked styles
														'&.Mui-checked': {
															transform: 'translateX(52px)',
															'& .MuiSwitch-thumb': {
																animation: 'rainbowGlow 10s ease-in-out infinite',
																border: '1px solid gold',

															},
															'& + .MuiSwitch-track': {
																backgroundColor: '#aab4be',
															},
														},
													},

													'& .MuiSwitch-track': {
														opacity: 1,
														backgroundColor: '#aab4be',
														borderRadius: 20 / 2,
													},
												}}
												checked={showBio} onChange={handleShowBio} />
										}
										label={showBio ? "Hide Biography" : "View Biography"}
										labelPlacement="bottom"
									/>
									{
										showBio && (
											<Box
												sx={{
													textAlign: "left",
													mt: 2,
													opacity: isPlaylistLoading ? 0 : 1,
													borderRadius: '25px',
													padding: '25px',
													backgroundColor: "rgba(69,69,69,0.69)",
													backdropFilter: 'blur(5px)'
												}}
												dangerouslySetInnerHTML={{
													__html: (displayedHero.bio).replace(/\\/g, '')
												}}
											/>
										)
									}
								</>
							) : (
								<Skeleton
									sx={{ bgcolor: "#FFF" }}
									variant="rectangular"
									animation="wave"
									width={256}
									height={144}
								/>
							)}
						</Container>
					</>
				)
			}

		</Box>
	);
};

// const Heroes: React.FC<{ sendDataToGrandParent: () => void }> = ({
const DotaSelector: React.FC<{ sendDataToGrandParent: (index: number) => void, playlistloading: boolean }> = ({
	sendDataToGrandParent, playlistloading
}) => {
	const dotaData = useDotaData();
	// if (!heroData) return <div>No data available</div>
	if (dotaData.status === "error") {
		console.error(`🥮grim__dotaselectorError==> ==> ==>dotaselectorError`);
		console.error({ grim__dotaselectorError: dotaData.error });
	}

	return (
		<Box
			sx={{
				flexGrow: 1
			}}
		>
			{dotaData.status === "loading" || dotaData.status === "init" && <Container>Loading...</Container>}
			{dotaData.status === "error" && (
				<Container>
					<h1>Sorry, something went wrong.</h1>
				</Container>
			)}
			{dotaData.status === "loaded" &&
				<HeroSelector
					herolist={dotaData}
					sendDataToParent={sendDataToGrandParent}
					playlistloading={playlistloading}
				/>}
			<br />
		</Box>
	);
};
export default React.memo(DotaSelector, (prevProps, nextProps) => {
	return (
		prevProps.playlistloading === nextProps.playlistloading &&
		prevProps.sendDataToGrandParent === nextProps.sendDataToGrandParent
	);
});
